import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe, DatePipe, Location } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { Observable } from 'rxjs';
import { DeclarationsAuthService } from 'src/app/auth/declarations-auth.service';
import { BasicUser } from 'src/app/core/basic-user.model';
import { UsersService } from 'src/app/core/users.service';
import { UkSMCR, UkSMCRToAdd, UkSMCRToAddFormConfig } from './uk-smcr.model';
import { UkSmcrService } from './uk-smcr.service';
import { UkSmcrAckDialogComponent } from './uk-smcr-ack-dialog.component';
import { SwitchModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { CheckBoxSelectionService, DropDownListModule, MultiSelectModule } from '@syncfusion/ej2-angular-dropdowns';
import { DialogModule } from '@syncfusion/ej2-angular-popups';

@Component({
  selector: 'ad-uk-smcr',
  templateUrl: './uk-smcr.component.html',
  styleUrls: ['./uk-smcr.component.css'],
  imports: [
    AsyncPipe,
    DatePipe,
    ReactiveFormsModule,
    TextBoxModule,
    DropDownListModule,
    DialogModule,
    UkSmcrAckDialogComponent,
    MultiSelectModule,
    SwitchModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [CheckBoxSelectionService]
})
export class UkSmcrComponent implements OnInit {
  public form!: FormGroup<UkSMCRToAddFormConfig>;
  public declarationDate!: Date;
  public hasLastYearData = false;
  public alreadySubmitted = false;
  public user!: BasicUser | null;
  public showAckDialog = false;
  public ackDialogData: any = {};

  public s2Q1HeldFunctionsOptions$!: Observable<string[]>;
  public s2Q1PrescribedResponsibilitiesOptions$!: Observable<string[]>;
  public s2Q2HeldCertificationFunctionsOptions$!: Observable<string[]>;

  public onBehalfOf = false;

  constructor(
    public authService: DeclarationsAuthService,
    private fb: FormBuilder,
    private usersService: UsersService,
    private service: UkSmcrService,
    private router: Router,
    private route: ActivatedRoute,
    public location: Location
  ) {}

  ngOnInit(): void {
    this.s2Q1HeldFunctionsOptions$ = this.service.getS2Q1FunctionOptions$();
    this.s2Q1PrescribedResponsibilitiesOptions$ = this.service.getS2Q1PrescribedResponsibilitiesOptions$();
    this.s2Q2HeldCertificationFunctionsOptions$ = this.service.getS2Q2Options$();

    this.form = this.fb.group<UkSMCRToAddFormConfig>({
      irn: this.fb.control<string | null>(null),
      s2Q1: this.fb.control<boolean | null>(false, Validators.required),
      s2Q1HeldFunctions: this.fb.control<string[] | null>(
        [],
        RxwebValidators.required({
          conditionalExpression: (x: any) => x.s2Q1 === true
        })
      ),
      s2Q1PrescribedResponsibilities: this.fb.control<string[] | null>([]),
      s2Q2: this.fb.control<boolean | null>(false, Validators.required),
      s2Q2HeldCertificationFunctions: this.fb.control<string[] | null>(
        [],
        RxwebValidators.required({
          conditionalExpression: (x: any) => x.s2Q2 === true
        })
      ),
      s2Q3a: this.fb.control<string | null>('', Validators.required),
      s2Q3b: this.fb.control<string | null>('', Validators.required),
      s2Q4: this.fb.control<string | null>('', Validators.required),
      s2Q5: this.fb.control<string | null>('', Validators.required),
      s2Q6: this.fb.control<string | null>('', Validators.required),
      s2Q7a: this.fb.control<string | null>('', Validators.required),
      s2Q7b: this.fb.control<string | null>('', Validators.required),
      s2Q8a: this.fb.control<string | null>('', Validators.required),
      s2Q8b: this.fb.control<string | null>('', Validators.required)
    });

    this.route.paramMap.subscribe((params) => {
      const email = params.get('email');

      if (email) {
        this.authService.getActiveAccount$().subscribe((account) => (this.onBehalfOf = email !== account?.username));
        this.service.getOne$(email).subscribe((data) => {
          if (data) {
            this.populateInitialData(data);
          } else {
            this.usersService.getOne$(email).subscribe((user) => (this.user = user));
          }
        });
      } else {
        this.service.getMine$().subscribe((data) => {
          if (data) {
            this.populateInitialData(data);
          } else {
            this.service.getMineFromLastYear$().subscribe((lastYearData) => {
              if (lastYearData) {
                this.hasLastYearData = true;
                this.populateInitialData(lastYearData);
              }
            });
          }
          this.declarationDate = this.declarationDate || new Date();
        });
        this.usersService.getMe$().subscribe((user) => (this.user = user));
      }
    });

    this.form.controls.s2Q1.valueChanges.subscribe((value) => {
      if (!value) {
        this.form.controls.s2Q1HeldFunctions.setValue([]);
        this.form.controls.s2Q1PrescribedResponsibilities.setValue([]);
      }
    });

    this.form.controls.s2Q2.valueChanges.subscribe((value) => {
      if (!value) {
        this.form.controls.s2Q2HeldCertificationFunctions.setValue([]);
      }
    });
  }

  public saveOrSubmitForm(): void {
    if (this.onBehalfOf) {
      this.saveFormOnBehalfOf();
    } else {
      this.submitFormAsTheUser();
    }
  }

  private submitFormAsTheUser(): void {
    this.ackDialogData = {
      name: this.user?.name,
      declarationDate: this.declarationDate
    };
    this.showAckDialog = true;
  }

  public handleDialogClose(data: any): void {
    if (data && data.name) {
      const value = {
        ...this.form.getRawValue(),
        corporateTitle: this.user!.title,
        employee: data.name,
        declarationDate: data.declarationDate || this.ackDialogData.declarationDate,
        userEmail: this.user!.email
      } as UkSMCRToAdd;

      this.form.reset();
      this.form.disable();

      this.service.acknowledge$(value).subscribe(() => this.router.navigate(['/']));
    }
    this.showAckDialog = false;
  }

  private saveFormOnBehalfOf(): void {
    const value = {
      ...this.form.getRawValue(),
      corporateTitle: this.user!.title,
      employee: this.user!.name,
      declarationDate: this.declarationDate,
      userEmail: this.user!.email
    } as UkSMCRToAdd;

    this.form.reset();
    this.form.disable();

    this.service.acknowledge$(value).subscribe(() => this.router.navigate(['/']));
  }

  private populateInitialData(data: UkSMCR): void {
    if (data) {
      this.form.controls.irn.setValue(data.irn);
      this.form.controls.s2Q1.setValue(data.s2Q1);
      this.form.controls.s2Q1HeldFunctions.setValue(data.s2Q1HeldFunctions);
      this.form.controls.s2Q1PrescribedResponsibilities.setValue(data.s2Q1PrescribedResponsibilities);
      this.form.controls.s2Q2.setValue(data.s2Q2);
      this.form.controls.s2Q2HeldCertificationFunctions.setValue(data.s2Q2HeldCertificationFunctions);
      this.form.controls.s2Q3a.setValue(data.s2Q3a);
      this.form.controls.s2Q3b.setValue(data.s2Q3b);
      this.form.controls.s2Q4.setValue(data.s2Q4);
      this.form.controls.s2Q5.setValue(data.s2Q5);
      this.form.controls.s2Q6.setValue(data.s2Q6);
      this.form.controls.s2Q7a.setValue(data.s2Q7a);
      this.form.controls.s2Q7b.setValue(data.s2Q7b);
      this.form.controls.s2Q8a.setValue(data.s2Q8a);
      this.form.controls.s2Q8b.setValue(data.s2Q8b);

      if (!this.hasLastYearData) {
        this.declarationDate = data.declarationDate;
        this.user = {
          name: data.employee,
          email: data.userEmail,
          title: data.corporateTitle
        };

        if (data.declarationDate) {
          this.alreadySubmitted = true;
          this.form.disable();
        }
      }
    }
  }
}
